import { Group, Select } from '@byecode/ui'
import { IndependentAction } from '@lighthouse/core'
import type { FlowNode, NodeTypes, NotificationActionConfig, VariableTree2 } from '@lighthouse/shared'
import {
    getAllCanBeUpstreamNodes,
    getFormDatasourceOptions,
    getImageVariableOption,
    getInputInnerTypeMatch,
    getUpstreamActionsOptions,
    getUpstreamNodesOptions,
    getViewVariableOption,
    ListItem4ByecodeUi,
    NodeFieldPreview,
    nodeTypeOptions,
    notificationStatusOptions,
    PAGE_LINK_OPTION,
    useAtomData
} from '@lighthouse/shared'
import React, { useCallback, useMemo } from 'react'

import { lastPageOfStackAtom } from '@/atoms/page/state'
import { workflowListAtom } from '@/atoms/workflow/state'
import { useFlowContainerContext } from '@/contexts/FlowContext'
import { useActionAdderDepParams } from '@/hooks/useActionAdderDepParams'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSource, useDataSourceList } from '@/hooks/useDataSource'
import { useIsDisabledWithVersion } from '@/hooks/useIsDisabledWithVersion'
import { useGetPageParameterAndVariableOptions, useGetUserRecordOptions } from '@/hooks/usePage'
import { useVariableCustomViewOption } from '@/hooks/useVariableCustomViewOption'

import * as SC from '../styles'

interface NotificationActionConfigureProps {
    nodeType?: NodeTypes
    value: NotificationActionConfig
    onChange: (value: NotificationActionConfig) => void
    allParentNodes?: FlowNode[]
    allParentActions?: IndependentAction[]
    actionTypeSwitcher?: React.ReactNode
}

export const NotificationActionConfigure: React.FC<NotificationActionConfigureProps> = ({
    nodeType,
    value,
    onChange,
    allParentNodes,
    allParentActions,
    actionTypeSwitcher
}) => {
    const disabledWithVersion = useIsDisabledWithVersion()
    const { type } = useFlowContainerContext()
    const isInFlow = !!type
    const isNotAutomation = type !== 'automation'

    const pageId = useAtomData(
        lastPageOfStackAtom,
        useCallback(s => (isInFlow ? '' : s?.pageId || ''), [isInFlow])
    )

    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const dataSourceList = useDataSourceList(appId, envId)
    const workflowList = useAtomData(workflowListAtom)
    const { viewDsId = '', formDsId = '', viewType, isImage } = useActionAdderDepParams()
    const formDataSource = useDataSource(appId, envId, formDsId)
    const viewDataSource = useDataSource(appId, envId, viewDsId)

    const upstreamOptions = useMemo(() => {
        if (isInFlow) {
            return getUpstreamNodesOptions({
                workflowList,
                upstreamNodes: getAllCanBeUpstreamNodes(allParentNodes || []),
                allUpstreamNodes: allParentNodes,
                dataSourceList
            })
        }
        return getUpstreamActionsOptions({
            workflowList,
            allParentActions: allParentActions || [],
            dataSourceList
        })
    }, [allParentActions, allParentNodes, dataSourceList, isInFlow, workflowList])

    const pageOptions = useGetPageParameterAndVariableOptions({ pageId })

    const formOption = useMemo(() => {
        if (formDataSource && !isInFlow) {
            return getFormDatasourceOptions({ dataSource: formDataSource })
        }
    }, [formDataSource, isInFlow])

    const imageOption = useMemo(() => {
        if (isImage) {
            return getImageVariableOption()
        }
    }, [isImage])

    const { customViewOption } = useVariableCustomViewOption()
    const viewOptions = useMemo(() => {
        if (isInFlow) {
            return
        }

        const options: VariableTree2[] = []
        if (viewDataSource) {
            const viewOption = getViewVariableOption({
                dataSource: viewDataSource,
                viewType
            })
            if (viewOption) {
                options.push(viewOption)

                return options
            }
        }
        if (customViewOption) {
            options.push(customViewOption)
        }
        return options
    }, [isInFlow, customViewOption, viewDataSource, viewType])

    const userOption = useGetUserRecordOptions()

    return (
        <SC.Container>
            <Group label="动作配置">
                <ListItem4ByecodeUi>
                    <SC.FormItemLabelWrapper>
                        <SC.FormItemLabel>类型</SC.FormItemLabel>
                    </SC.FormItemLabelWrapper>
                    <SC.FormItemContent>
                        {actionTypeSwitcher || <Select disabled value={nodeType} options={nodeTypeOptions} />}
                    </SC.FormItemContent>
                </ListItem4ByecodeUi>
                <ListItem4ByecodeUi>
                    <SC.FormItemLabelWrapper>
                        <SC.FormItemLabel required>内容</SC.FormItemLabel>
                    </SC.FormItemLabelWrapper>
                    <SC.FormItemContent>
                        <NodeFieldPreview
                            innerTypes={getInputInnerTypeMatch('TEXT')}
                            disabled={disabledWithVersion}
                            autoHeight
                            value={value.notificationContent}
                            onBlur={notificationContent => onChange({ ...value, notificationContent })}
                            viewOptions={viewOptions}
                            formOption={formOption}
                            pageOptions={isNotAutomation ? [PAGE_LINK_OPTION, ...(pageOptions || [])] : undefined}
                            upstreamOptions={upstreamOptions}
                            userOption={userOption}
                            dataSourceList={dataSourceList}
                            imageOption={imageOption}
                        />
                    </SC.FormItemContent>
                </ListItem4ByecodeUi>
                <ListItem4ByecodeUi>
                    <SC.FormItemLabelWrapper>
                        <SC.FormItemLabel>提示类型</SC.FormItemLabel>
                    </SC.FormItemLabelWrapper>
                    <SC.FormItemContent>
                        <Select
                            disabledPortal
                            value={value.notificationStatus || 'INFO'}
                            options={notificationStatusOptions}
                            onChange={notificationStatus =>
                                onChange({
                                    ...value,
                                    notificationStatus: notificationStatus as 'SUCCESS' | 'ERROR' | 'INFO'
                                })
                            }
                        />
                    </SC.FormItemContent>
                </ListItem4ByecodeUi>
            </Group>
        </SC.Container>
    )
}
